import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { 
  ArrowLeft, 
  ArrowRight, 
  Save, 
  Upload, 
  Smartphone, 
  Palette, 
  Settings, 
  CheckCircle,
  X,
  Eye,
  Zap
} from 'lucide-react'
import { AppConfigFormData, Template, Platform } from '../types'
import { mockApiCalls, mockTemplates } from '../utils/mockData'

interface FormStep {
  id: string
  title: string
  icon: React.ReactNode
  description: string
}

const FORM_STEPS: FormStep[] = [
  {
    id: 'basic',
    title: 'Basic Information',
    icon: <Settings className="w-5 h-5" />,
    description: 'App name, description, and company details'
  },
  {
    id: 'web',
    title: 'Web Configuration',
    icon: <Smartphone className="w-5 h-5" />,
    description: 'Your website URL and domain settings'
  },
  {
    id: 'platforms',
    title: 'Platform Selection',
    icon: <Smartphone className="w-5 h-5" />,
    description: 'Choose target platforms and features'
  },
  {
    id: 'branding',
    title: 'Branding & Design',
    icon: <Palette className="w-5 h-5" />,
    description: 'Colors, icons, and visual identity'
  },
  {
    id: 'assets',
    title: 'Assets & Media',
    icon: <Upload className="w-5 h-5" />,
    description: 'Upload app icons and splash screens'
  },
  {
    id: 'review',
    title: 'Review & Build',
    icon: <CheckCircle className="w-5 h-5" />,
    description: 'Review configuration and start build'
  }
]

export function ConfigureApp() {
  const navigate = useNavigate()
  const [currentStep, setCurrentStep] = useState(0)
  const [formData, setFormData] = useState<AppConfigFormData>({
    name: '',
    description: '',
    companyName: 'Ser.vi',
    startUrl: '',
    domain: '',
    packageName: '',
    version: '1.0.0',
    primaryColor: '#3b82f6',
    accentColor: '#f59e0b',
    backgroundColor: '#ffffff',
    features: {
      locationServices: true,
      pushNotifications: true,
      cameraAccess: true,
      offlineMode: false,
      analytics: true
    },
    platforms: ['ios', 'android'],
    environment: 'development'
  })
  const [templates, setTemplates] = useState<Template[]>([])
  const [loading, setLoading] = useState(false)
  const [errors, setErrors] = useState<Record<string, string>>({})

  useEffect(() => {
    const loadTemplates = async () => {
      const templateData = await mockApiCalls.getTemplates()
      setTemplates(templateData)
    }
    loadTemplates()
  }, [])

  const updateFormData = (updates: Partial<AppConfigFormData>) => {
    setFormData(prev => {
      const updated = { ...prev, ...updates }
      
      // Auto-generate package name when app name changes
      if (updates.name && updates.name.trim()) {
        const appName = updates.name.toLowerCase().replace(/[^a-z0-9]/g, '')
        updated.packageName = `com.servi.${appName}`
      }
      
      return updated
    })
    
    // Clear errors for updated fields
    setErrors(prev => {
      const newErrors = { ...prev }
      Object.keys(updates).forEach(key => {
        delete newErrors[key]
      })
      return newErrors
    })
  }

  const validateStep = (stepIndex: number): boolean => {
    const newErrors: Record<string, string> = {}
    
    switch (stepIndex) {
      case 0: // Basic Information
        if (!formData.name.trim()) newErrors.name = 'App name is required'
        if (!formData.companyName.trim()) newErrors.companyName = 'Company name is required'
        if (!formData.description.trim()) newErrors.description = 'Description is required'
        break
        
      case 1: // Web Configuration
        if (!formData.startUrl.trim()) newErrors.startUrl = 'Start URL is required'
        if (!formData.domain.trim()) newErrors.domain = 'Domain is required'
        try {
          new URL(formData.startUrl)
        } catch {
          newErrors.startUrl = 'Please enter a valid URL'
        }
        break
        
      case 2: // Platform Selection
        if (formData.platforms.length === 0) newErrors.platforms = 'Select at least one platform'
        if (!formData.packageName.trim()) newErrors.packageName = 'Package/Bundle ID is required'
        break
        
      case 3: // Branding
        if (!formData.primaryColor) newErrors.primaryColor = 'Primary color is required'
        if (!formData.accentColor) newErrors.accentColor = 'Accent color is required'
        break
    }
    
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const nextStep = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => Math.min(prev + 1, FORM_STEPS.length - 1))
    }
  }

  const prevStep = () => {
    setCurrentStep(prev => Math.max(prev - 1, 0))
  }

  const generatePackageName = () => {
    const domain = formData.domain.replace(/^https?:\/\//, '').replace(/\/$/, '')
    const appName = formData.name.toLowerCase().replace(/[^a-z0-9]/g, '')
    const domainParts = domain.split('.').reverse()
    return `${domainParts.join('.')}.${appName}`
  }

  const handleSubmit = async () => {
    if (!validateStep(currentStep)) return
    
    setLoading(true)
    try {
      const newApp = await mockApiCalls.createApp(formData)
      navigate(`/apps/${newApp.id}`)
    } catch (error) {
      console.error('Failed to create app:', error)
    } finally {
      setLoading(false)
    }
  }

  const renderStepContent = () => {
    switch (currentStep) {
      case 0: // Basic Information
        return (
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                App Name *
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => updateFormData({ name: e.target.value })}
                className={`input ${errors.name ? 'border-error-300' : ''}`}
                placeholder="My Awesome App"
              />
              {errors.name && <p className="mt-1 text-sm text-error-600">{errors.name}</p>}
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Description *
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => updateFormData({ description: e.target.value })}
                rows={3}
                className={`input ${errors.description ? 'border-error-300' : ''}`}
                placeholder="Describe what your app does..."
              />
              {errors.description && <p className="mt-1 text-sm text-error-600">{errors.description}</p>}
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Company Name *
              </label>
              <input
                type="text"
                value={formData.companyName}
                onChange={(e) => updateFormData({ companyName: e.target.value })}
                className={`input ${errors.companyName ? 'border-error-300' : ''}`}
                placeholder="My Company LLC"
              />
              {errors.companyName && <p className="mt-1 text-sm text-error-600">{errors.companyName}</p>}
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Version
              </label>
              <input
                type="text"
                value={formData.version}
                onChange={(e) => updateFormData({ version: e.target.value })}
                className="input"
                placeholder="1.0.0"
              />
            </div>
          </div>
        )
        
      case 1: // Web Configuration
        return (
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Website URL *
              </label>
              <input
                type="url"
                value={formData.startUrl}
                onChange={(e) => updateFormData({ startUrl: e.target.value })}
                className={`input ${errors.startUrl ? 'border-error-300' : ''}`}
                placeholder="https://mywebsite.com"
              />
              {errors.startUrl && <p className="mt-1 text-sm text-error-600">{errors.startUrl}</p>}
              <p className="mt-1 text-sm text-gray-500">
                This is the initial URL that will load in your app
              </p>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Domain *
              </label>
              <input
                type="text"
                value={formData.domain}
                onChange={(e) => updateFormData({ domain: e.target.value })}
                className={`input ${errors.domain ? 'border-error-300' : ''}`}
                placeholder="mywebsite.com"
              />
              {errors.domain && <p className="mt-1 text-sm text-error-600">{errors.domain}</p>}
              <p className="mt-1 text-sm text-gray-500">
                Your website's domain (without http/https)
              </p>
            </div>
          </div>
        )
        
      case 2: // Platform Selection
        return (
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-4">
                Target Platforms *
              </label>
              <div className="space-y-3">
                {templates.map((template) => (
                  <div
                    key={template.id}
                    className={`border-2 rounded-lg p-4 cursor-pointer transition-all ${
                      formData.platforms.includes(template.platform)
                        ? 'border-brand-500 bg-brand-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                    onClick={() => {
                      const isSelected = formData.platforms.includes(template.platform)
                      updateFormData({
                        platforms: isSelected
                          ? formData.platforms.filter(p => p !== template.platform)
                          : [...formData.platforms, template.platform]
                      })
                    }}
                  >
                    <div className="flex items-start space-x-3">
                      <div className={`mt-1 w-5 h-5 rounded border-2 flex items-center justify-center ${
                        formData.platforms.includes(template.platform)
                          ? 'border-brand-500 bg-brand-500'
                          : 'border-gray-300'
                      }`}>
                        {formData.platforms.includes(template.platform) && (
                          <CheckCircle className="w-3 h-3 text-white" />
                        )}
                      </div>
                      <div className="flex-1">
                        <h3 className="font-medium text-gray-900">{template.name}</h3>
                        <p className="text-sm text-gray-600 mt-1">{template.description}</p>
                        <div className="flex flex-wrap gap-2 mt-2">
                          {template.features.slice(0, 3).map((feature) => (
                            <span key={feature} className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded">
                              {feature}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              {errors.platforms && <p className="mt-1 text-sm text-error-600">{errors.platforms}</p>}
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Package/Bundle ID *
              </label>
              <div className="flex space-x-2">
                <input
                  type="text"
                  value={formData.packageName}
                  onChange={(e) => updateFormData({ packageName: e.target.value })}
                  className={`input flex-1 ${errors.packageName ? 'border-error-300' : ''}`}
                  placeholder="com.company.appname"
                />
                <button
                  type="button"
                  onClick={() => updateFormData({ packageName: generatePackageName() })}
                  className="px-3 py-2 text-sm bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                  title="Auto-generate"
                >
                  <Zap className="w-4 h-4" />
                </button>
              </div>
              {errors.packageName && <p className="mt-1 text-sm text-error-600">{errors.packageName}</p>}
              <p className="mt-1 text-sm text-gray-500">
                Unique identifier for your app (e.g., com.company.appname)
              </p>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-4">
                Features
              </label>
              <div className="space-y-3">
                {Object.entries({
                  locationServices: 'Location Services',
                  pushNotifications: 'Push Notifications',
                  cameraAccess: 'Camera Access',
                  offlineMode: 'Offline Mode',
                  analytics: 'Analytics'
                }).map(([key, label]) => (
                  <label key={key} className="flex items-center space-x-3">
                    <input
                      type="checkbox"
                      checked={formData.features[key as keyof typeof formData.features]}
                      onChange={(e) => updateFormData({
                        features: {
                          ...formData.features,
                          [key]: e.target.checked
                        }
                      })}
                      className="w-4 h-4 text-brand-600 border-gray-300 rounded focus:ring-brand-500"
                    />
                    <span className="text-sm text-gray-700">{label}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>
        )
        
      case 3: // Branding
        return (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Primary Color *
                </label>
                <div className="flex space-x-2">
                  <input
                    type="color"
                    value={formData.primaryColor}
                    onChange={(e) => updateFormData({ primaryColor: e.target.value })}
                    className="w-12 h-10 border border-gray-300 rounded cursor-pointer"
                  />
                  <input
                    type="text"
                    value={formData.primaryColor}
                    onChange={(e) => updateFormData({ primaryColor: e.target.value })}
                    className={`input flex-1 ${errors.primaryColor ? 'border-error-300' : ''}`}
                    placeholder="#3b82f6"
                  />
                </div>
                {errors.primaryColor && <p className="mt-1 text-sm text-error-600">{errors.primaryColor}</p>}
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Accent Color *
                </label>
                <div className="flex space-x-2">
                  <input
                    type="color"
                    value={formData.accentColor}
                    onChange={(e) => updateFormData({ accentColor: e.target.value })}
                    className="w-12 h-10 border border-gray-300 rounded cursor-pointer"
                  />
                  <input
                    type="text"
                    value={formData.accentColor}
                    onChange={(e) => updateFormData({ accentColor: e.target.value })}
                    className={`input flex-1 ${errors.accentColor ? 'border-error-300' : ''}`}
                    placeholder="#f59e0b"
                  />
                </div>
                {errors.accentColor && <p className="mt-1 text-sm text-error-600">{errors.accentColor}</p>}
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Background Color
                </label>
                <div className="flex space-x-2">
                  <input
                    type="color"
                    value={formData.backgroundColor}
                    onChange={(e) => updateFormData({ backgroundColor: e.target.value })}
                    className="w-12 h-10 border border-gray-300 rounded cursor-pointer"
                  />
                  <input
                    type="text"
                    value={formData.backgroundColor}
                    onChange={(e) => updateFormData({ backgroundColor: e.target.value })}
                    className="input flex-1"
                    placeholder="#ffffff"
                  />
                </div>
              </div>
            </div>
            
            {/* Color Preview */}
            <div className="bg-gray-50 rounded-lg p-6">
              <h4 className="text-sm font-medium text-gray-700 mb-4">Preview</h4>
              <div className="flex space-x-4">
                <div 
                  className="w-16 h-16 rounded-lg flex items-center justify-center text-white font-medium text-lg"
                  style={{ backgroundColor: formData.primaryColor }}
                >
                  Aa
                </div>
                <div 
                  className="w-16 h-16 rounded-lg flex items-center justify-center text-white font-medium text-lg"
                  style={{ backgroundColor: formData.accentColor }}
                >
                  Bb
                </div>
                <div 
                  className="w-16 h-16 rounded-lg border-2 border-gray-200 flex items-center justify-center text-gray-700 font-medium text-lg"
                  style={{ backgroundColor: formData.backgroundColor }}
                >
                  Cc
                </div>
              </div>
            </div>
          </div>
        )
        
      case 4: // Assets
        return (
          <div className="space-y-6">
            <div className="text-center">
              <Upload className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">Upload Assets</h3>
              <p className="mt-1 text-sm text-gray-500">
                App icons and splash screens will be uploaded here
              </p>
              <div className="mt-4">
                <button
                  type="button"
                  className="btn-outline"
                  onClick={() => {
                    // Asset upload functionality would be implemented here
                    alert('Asset upload functionality would be implemented here')
                  }}
                >
                  <Upload className="w-4 h-4 mr-2" />
                  Choose Files
                </button>
              </div>
            </div>
            
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h4 className="text-sm font-medium text-blue-900 mb-2">Asset Requirements</h4>
              <ul className="text-sm text-blue-800 space-y-1">
                <li>• App Icon: PNG format, 1024x1024px minimum</li>
                <li>• Splash Screen: PNG format, various sizes will be generated</li>
                <li>• Maximum file size: 10MB per file</li>
                <li>• Assets can be uploaded after app creation</li>
              </ul>
            </div>
          </div>
        )
        
      case 5: // Review
        return (
          <div className="space-y-6">
            <h3 className="text-lg font-medium text-gray-900">Review Configuration</h3>
            
            <div className="bg-gray-50 rounded-lg p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <h4 className="text-sm font-medium text-gray-700">App Name</h4>
                  <p className="text-sm text-gray-900">{formData.name}</p>
                </div>
                <div>
                  <h4 className="text-sm font-medium text-gray-700">Company</h4>
                  <p className="text-sm text-gray-900">{formData.companyName}</p>
                </div>
                <div>
                  <h4 className="text-sm font-medium text-gray-700">Website URL</h4>
                  <p className="text-sm text-gray-900">{formData.startUrl}</p>
                </div>
                <div>
                  <h4 className="text-sm font-medium text-gray-700">Package ID</h4>
                  <p className="text-sm text-gray-900">{formData.packageName}</p>
                </div>
                <div>
                  <h4 className="text-sm font-medium text-gray-700">Platforms</h4>
                  <p className="text-sm text-gray-900">
                    {formData.platforms.map(p => p === 'ios' ? 'iOS' : 'Android').join(', ')}
                  </p>
                </div>
                <div>
                  <h4 className="text-sm font-medium text-gray-700">Primary Color</h4>
                  <div className="flex items-center space-x-2">
                    <div 
                      className="w-4 h-4 rounded"
                      style={{ backgroundColor: formData.primaryColor }}
                    ></div>
                    <span className="text-sm text-gray-900">{formData.primaryColor}</span>
                  </div>
                </div>
              </div>
              
              <div>
                <h4 className="text-sm font-medium text-gray-700 mb-2">Features</h4>
                <div className="flex flex-wrap gap-2">
                  {Object.entries(formData.features)
                    .filter(([_, enabled]) => enabled)
                    .map(([feature, _]) => (
                      <span key={feature} className="px-2 py-1 bg-brand-100 text-brand-800 text-xs rounded">
                        {feature.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                      </span>
                    ))
                  }
                </div>
              </div>
            </div>
            
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <h4 className="text-sm font-medium text-yellow-800 mb-2">Next Steps</h4>
              <ul className="text-sm text-yellow-700 space-y-1">
                <li>• Your app configuration will be saved</li>
                <li>• You can upload assets and start builds from the app dashboard</li>
                <li>• Build process typically takes 5-15 minutes per platform</li>
              </ul>
            </div>
          </div>
        )
        
      default:
        return null
    }
  }

  return (
    <div className="max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex items-center space-x-4 mb-8">
        <button
          onClick={() => navigate('/')}
          className="p-2 text-gray-400 hover:text-gray-600 rounded-lg"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Configure New App</h1>
          <p className="text-gray-600">Set up your app configuration and build settings</p>
        </div>
      </div>

      {/* Progress Steps */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          {FORM_STEPS.map((step, index) => (
            <div
              key={step.id}
              className={`flex items-center ${index < FORM_STEPS.length - 1 ? 'flex-1' : ''}`}
            >
              <div className="flex items-center">
                <div
                  className={`w-10 h-10 rounded-full flex items-center justify-center border-2 transition-all ${
                    index <= currentStep
                      ? 'border-brand-500 bg-brand-500 text-white'
                      : 'border-gray-300 bg-white text-gray-400'
                  }`}
                >
                  {index < currentStep ? (
                    <CheckCircle className="w-5 h-5" />
                  ) : (
                    step.icon
                  )}
                </div>
                <div className="ml-3 hidden lg:block">
                  <h3 className={`text-sm font-medium ${
                    index <= currentStep ? 'text-gray-900' : 'text-gray-400'
                  }`}>
                    {step.title}
                  </h3>
                  <p className="text-xs text-gray-500">{step.description}</p>
                </div>
              </div>
              {index < FORM_STEPS.length - 1 && (
                <div
                  className={`flex-1 h-0.5 mx-4 ${
                    index < currentStep ? 'bg-brand-500' : 'bg-gray-300'
                  }`}
                ></div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Form Content */}
      <div className="card p-8 mb-8">
        <div className="mb-6">
          <h2 className="text-xl font-semibold text-gray-900">
            {FORM_STEPS[currentStep].title}
          </h2>
          <p className="text-gray-600 mt-1">
            {FORM_STEPS[currentStep].description}
          </p>
        </div>

        {renderStepContent()}
      </div>

      {/* Navigation */}
      <div className="flex justify-between">
        <button
          onClick={prevStep}
          disabled={currentStep === 0}
          className="btn-outline flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <ArrowLeft className="w-4 h-4" />
          <span>Previous</span>
        </button>

        <div className="flex space-x-3">
          <button
            onClick={() => {
              // Save as draft functionality
              alert('Save as draft functionality would be implemented here')
            }}
            className="btn-ghost flex items-center space-x-2"
          >
            <Save className="w-4 h-4" />
            <span>Save Draft</span>
          </button>

          {currentStep < FORM_STEPS.length - 1 ? (
            <button
              onClick={nextStep}
              className="btn-primary flex items-center space-x-2"
            >
              <span>Next</span>
              <ArrowRight className="w-4 h-4" />
            </button>
          ) : (
            <button
              onClick={handleSubmit}
              disabled={loading}
              className="btn-primary flex items-center space-x-2"
            >
              {loading ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  <span>Creating...</span>
                </>
              ) : (
                <>
                  <CheckCircle className="w-4 h-4" />
                  <span>Create App</span>
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  )
} 